import React, { useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { Box, Button, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LOAD_CONTRIBUTIONS,
  newCapitalContribution,
  loadContributions
} from 'src/store/actions/contractActions';
import PropTypes from 'prop-types';
import {
  isLoadingSelector,
  successSelector
} from 'src/store/selectors/statusSelector';
import { selectContributions } from 'src/store/selectors/contractSelectors';
import Add from '@mui/icons-material/Add';
import {
  isContractInGracePeriod,
  isCurrentMonthAndYear
} from 'src/utils/common';
import useStyles from '../styles';
import ContributionsList from './ContributionsList';
import ContractStatus from '../../../../utils/constants/contracts';
import ContributionsForm from './CreateContributionForm';
import CapitalContributionSimulationDialog from './CapitalContributionSimulationDialog';

const Contributions = ({ contract }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const [open, setOpen] = React.useState(false);
  const [openSimulation, setOpenSimulation] = React.useState(false);

  const contributions = useSelector(selectContributions);
  const loading = useSelector(isLoadingSelector([LOAD_CONTRIBUTIONS.action]));
  const success = useSelector(successSelector([LOAD_CONTRIBUTIONS.action]));
  const disabledContribution =
    contract.status === ContractStatus.ENDED ||
    isCurrentMonthAndYear(contract.endDate) ||
    isCurrentMonthAndYear(contract.startDate) ||
    isContractInGracePeriod(contract);

  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleOpenSimulation = useCallback(() => {
    setOpenSimulation(true);
  }, [setOpenSimulation]);

  const handleCloseSimulation = useCallback(() => {
    setOpenSimulation(false);
  }, [setOpenSimulation]);

  const handleSubmit = useCallback(
    values => {
      dispatch(newCapitalContribution(Number(id), values, handleClose));
    },
    [setOpen]
  );

  useEffect(() => {
    dispatch(loadContributions(id));
  }, [dispatch, id]);

  return (
    <Box mt={3} display="flex" flexDirection="column">
      <Box
        className={classes.actionsContainer}
        lg={12}
        flex
        justifyContent="flex-end"
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            className={clsx(classes.buttons)}
            variant="outlined"
            color="primary"
            onClick={handleOpenSimulation}
          >
            {t('CONTRACT_VIEW.SIMULATE_CONTRIBUTION')}
          </Button>
          <Tooltip
            title={
              disabledContribution && t('CONTRACT_VIEW.CONTRIBUTION_DISABLED')
            }
          >
            <span>
              <Button
                className={clsx(classes.buttons)}
                variant="contained"
                color="primary"
                onClick={handleClickOpen}
                disabled={disabledContribution}
              >
                <Add sx={{ marginRight: 1 }} />
                {t('CONTRACT_VIEW.NEW_CONTRIBUTION')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Box lg={12}>
        <ContributionsList
          contributions={contributions}
          loading={loading}
          success={success}
        />
        <ContributionsForm
          open={open}
          handleClose={handleClose}
          handleCreateContribution={handleSubmit}
          contractId={id}
          currentRent={contract.rent}
          equipmentId={contract.equipmentId}
        />
        <CapitalContributionSimulationDialog
          open={openSimulation}
          onClose={handleCloseSimulation}
          contractId={id}
        />
      </Box>
    </Box>
  );
};

Contributions.propTypes = {
  contract: PropTypes.object.isRequired
};

export default Contributions;
